
import {
  defineComponent, ref, computed, PropType, watch, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { getTemplatesNotUsedBySyncElement, getCampaignAutomationsWithTemplatesNotUsedBySyncElement } from '@/composables/template/SyncElement';
import Filter from '@/components/table/Filter.vue';
import { isDateInRange } from '@/helpers/Date';
import TemplateDataTable from './TemplateDataTable.vue';

// Extended interface to include name_marketing_workflow and date_creation from GraphQL response
interface ExtendedTemplatesCustom extends TemplatesCustom {
  name_campaign_automation?: string;
  date_creation?: string;
  date_modification?: string;
}

interface MarketingWorkflow {
  id_campaign_automation: number;
  name: string;
  date_modification: string;
  templates: ExtendedTemplatesCustom[];
}

interface TemplateGroup {
  groupName: string;
  icon: string;
  templates: ExtendedTemplatesCustom[];
  dateCreation?: string;
  isLoading?: boolean;
  selectedTemplates?: ExtendedTemplatesCustom[];
  isWorkflowGroup?: boolean;
  workflowId?: number;
}

export default defineComponent({
  name: 'TemplateListView',

  components: {
    DataTable,
    Column,
    Button,
    ProgressSpinner,
    TemplateDataTable,
    Filter,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idShop: {
      type: Number,
      required: true,
    },

    syncElementId: {
      type: String,
      required: true,
    },

    templateType: {
      type: String,
      required: true,
    },

    elementPartTypeOrSubtype: {
      type: String,
      required: true,
    },

    isPartType: {
      type: Boolean,
      required: true,
    },

    selectedTemplates: {
      type: Set as PropType<Set<number>>,
      required: true,
    },

    selectedSectionsByTemplate: {
      type: Map as PropType<Map<number, string[]>>,
      required: true,
    },

    excludeIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },

    fetchedTemplates: {
      type: Array as PropType<ExtendedTemplatesCustom[]>,
      default: () => [],
    },

    fetchedWorkflows: {
      type: Array as PropType<MarketingWorkflow[]>,
      default: () => [],
    },

    version: {
      type: Number,
      default: undefined,
    },
  },

  emits: ['preview-template', 'selection-changed', 'open-section-modal', 'fetched-templates', 'fetched-workflows'],

  expose: ['handleSectionsSelected'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const expandedRows = ref<TemplateGroup[]>([]);
    const templatesData = ref<ExtendedTemplatesCustom[]>([]);
    const workflowsData = ref<MarketingWorkflow[]>([]);
    const isLoading = ref(false);
    const isLoadingWorkflows = ref(false);
    const lastWorkflowId = ref<number | undefined>(undefined);
    const lastCreationDate = ref<string | undefined>(undefined);
    const localSelectedTemplates = ref<number[]>(Array.from(props.selectedTemplates));
    const lastEmittedSelection = ref<number[]>(Array.from(props.selectedTemplates));
    const totalRecords = ref(0);

    // Group filtering system
    const activeGroupFilters = ref<Set<string>>(new Set());
    const groupColumns = [
      {
        field: 'groupName',
        header: t('templateBuilder.saveSyncElementModal.templateListView.columns.name'),
        type: 'text',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 60%',
        matchModes: [
          { label: t('filters.contains'), value: 'contains' },
          { label: t('filters.startsWith'), value: 'startsWith' },
          { label: t('filters.endsWith'), value: 'endsWith' },
          { label: t('filters.equals'), value: 'equals' },
        ],
      },
      {
        field: 'dateCreation',
        header: t('campaigns.bulk.list.columns.last_update'),
        type: 'date',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 40%',
        filterSettings: { type: 'date' },
        matchModes: [
          { label: t('filters.interval'), value: 'interval' },
        ],
      },
    ];

    // Initialize filter params for group DataTable
    const groupFilterParams = ref<Record<string, any>>(groupColumns
      .filter((col) => col.filterable)
      .reduce<object>((acc, col) => {
        let matchMode = 'contains';
        if (col.filterSettings?.type === 'date') {
          matchMode = 'interval';
        }
        return ({
          ...acc,
          [col.field]: {
            operator: null,
            constraints: [{ value: '', matchMode }],
          },
        });
      }, {}));

    // Watch for changes in selectedTemplates prop to sync with local state
    watch(() => props.selectedTemplates, (newSelection) => {
      localSelectedTemplates.value = Array.from(newSelection);
      lastEmittedSelection.value = Array.from(newSelection);
    }, { deep: true });

    const onTemplateSelectionChanged = () => {
      // Avoid emitting if selection hasn't actually changed
      const currentSelection = [...localSelectedTemplates.value].sort();
      const lastSelection = [...lastEmittedSelection.value].sort();

      if (JSON.stringify(currentSelection) === JSON.stringify(lastSelection)) {
        return;
      }

      lastEmittedSelection.value = [...localSelectedTemplates.value];

      emit('selection-changed', {
        selectedIds: localSelectedTemplates.value,
        selectedSectionsByTemplate: Object.fromEntries(props.selectedSectionsByTemplate),
      });
    };

    // Handle sections selection from parent
    const handleSectionsSelected = (templateId: number, selectedSections: string[]) => {
      // The parent component handles the section selection state
      // We just need to update the local template selection

      // If sections are selected, mark the template as selected
      if (selectedSections.length > 0) {
        if (!localSelectedTemplates.value.includes(templateId)) {
          localSelectedTemplates.value = [...localSelectedTemplates.value, templateId];
        }
      } else {
        // If no sections selected, remove template from selection
        localSelectedTemplates.value = localSelectedTemplates.value.filter((id) => id !== templateId);
      }

      // Emit the updated selection
      onTemplateSelectionChanged();
    };

    // Load templates data
    const loadTemplatesData = async () => {
      if (isLoading.value) return;

      if (props.fetchedTemplates.length > 0) {
        templatesData.value = props.fetchedTemplates;
        totalRecords.value = props.fetchedTemplates.length;
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      try {
        const result = await getTemplatesNotUsedBySyncElement({
          idShop: props.idShop,
          syncElementId: props.syncElementId,
          templateType: props.templateType,
          elementPartTypeOrSubtype: props.elementPartTypeOrSubtype,
          isPartType: props.isPartType,
          excludeAutomationTemplates: true,
          limit: -1,
          // lastTemplateId: currentCursor, // No longer needed
          excludeIds: props.excludeIds,
          version: props.version,
        });

        const newTemplates = result.items as ExtendedTemplatesCustom[];
        templatesData.value = newTemplates;
        totalRecords.value = result.total;

        emit('fetched-templates', newTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const loadWorkflowsData = async () => {
      if (isLoadingWorkflows.value) return;

      if (props.fetchedWorkflows.length > 0) {
        workflowsData.value = props.fetchedWorkflows;
        isLoadingWorkflows.value = false;
        return;
      }

      isLoadingWorkflows.value = true;
      try {
        const result = await getCampaignAutomationsWithTemplatesNotUsedBySyncElement({
          idShop: props.idShop,
          syncElementId: props.syncElementId,
          templateType: props.templateType,
          elementPartTypeOrSubtype: props.elementPartTypeOrSubtype,
          isPartType: props.isPartType,
          limit: -1,
          lastWorkflowId: lastWorkflowId.value,
          lastCreationDate: lastCreationDate.value,
          excludeIds: props.excludeIds,
          version: props.version,
        });

        const newWorkflows = result.items as MarketingWorkflow[];
        workflowsData.value = newWorkflows;

        emit('fetched-workflows', newWorkflows);
      } catch (error) {
        console.error('Error loading workflows:', error);
      } finally {
        isLoadingWorkflows.value = false;
      }
    };

    // Filtered groups storage (reactive)
    const filteredGroupedTemplates = ref<TemplateGroup[]>([]);

    // Group templates by category
    const groupedTemplates = computed((): TemplateGroup[] => {
      const groups: TemplateGroup[] = [];

      // Add "Mes modèles" group
      groups.push({
        groupName: t('templateBuilder.saveSyncElementModal.templateListView.groups.templates'),
        icon: 'far fa-table',
        templates: templatesData.value,
        isLoading: isLoading.value,
        isWorkflowGroup: false,
      });

      // Add Marketing Workflows groups
      workflowsData.value.forEach((workflow) => {
        groups.push({
          groupName: workflow.name,
          icon: 'far fa-share-nodes',
          templates: workflow.templates,
          dateCreation: workflow.date_modification,
          isLoading: false,
          isWorkflowGroup: true,
          workflowId: workflow.id_campaign_automation,
        });
      });

      return groups;
    });

    // Filter function for groups (like SpmTable.vue filterClientSideTableData)
    const filterClientSideGroupData = () => {
      let groups = groupedTemplates.value;

      // Check if any filter is active
      const hasActiveFilters = Object.values(groupFilterParams.value).some((filter) => {
        const constraintValue = filter.constraints[0].value;
        return constraintValue !== '' && constraintValue !== null && constraintValue !== undefined;
      });

      // If filters are active, remove "Mes modèles" group and apply filters
      if (hasActiveFilters) {
        groups = groups.filter((group) => group.isWorkflowGroup === true);

        groups = groups.filter((group) => Object.entries(groupFilterParams.value).every(([column, filter]) => {
          const { matchMode: filterMatchMode, value: filterConstraintValue } = filter.constraints[0];
          const columnValue = String(group[column as keyof typeof group] || '');

          if (
            filterConstraintValue === ''
              || (Array.isArray(filterConstraintValue) && filterConstraintValue.length === 0)
          ) return true;

          const filterValueStr = String(filterConstraintValue).toLowerCase();
          const columnValueStr = columnValue.toLowerCase();

          if (filterMatchMode === 'startsWith') {
            return columnValueStr.startsWith(filterValueStr);
          }

          if (filterMatchMode === 'endsWith') {
            return columnValueStr.endsWith(filterValueStr);
          }

          if (filterMatchMode === 'equals') {
            return columnValueStr === filterValueStr;
          }

          if (filterMatchMode === 'interval' && column === 'dateCreation') {
            // Handle date filtering for interval mode
            return isDateInRange(columnValue, filterConstraintValue);
          }

          // Default to 'contains'
          return columnValueStr.includes(filterValueStr);
        }));
      }

      filteredGroupedTemplates.value = groups;
    };

    // Group filter functions
    const onGroupFilterChange = () => {
      // Handle filter changes if needed
    };

    const getGroupFilterStatus = (field: string) => (activeGroupFilters.value.has(field) ? 'column-filter-active' : '');

    const clearGroupFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Clear the filterModel constraints directly
      if (filterModel.constraints && filterModel.constraints[0]) {
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].value = '';
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].matchMode = field === 'dateCreation' ? 'interval' : 'contains';
      }

      // Force reactivity by creating new object
      const newFilterParams = { ...groupFilterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: '',
            matchMode: field === 'dateCreation' ? 'interval' : 'contains',
          }],
        };
      }
      groupFilterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideGroupData();

      activeGroupFilters.value.delete(field);
      filterCallback();
    };

    const applyGroupFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Read from filterModel constraints
      const filterConstraintValue = filterModel.constraints[0].value;
      const filterMatchMode = filterModel.constraints[0].matchMode;

      // Force reactivity by creating new object
      const newFilterParams = { ...groupFilterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: filterConstraintValue,
            matchMode: filterMatchMode,
          }],
        };
      }
      groupFilterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideGroupData();

      // Manage visual indication
      if (filterConstraintValue && (typeof filterConstraintValue === 'object' || String(filterConstraintValue).trim() !== '')) {
        activeGroupFilters.value.add(field);
      } else {
        activeGroupFilters.value.delete(field);
      }

      filterCallback();
    };

    const handlePreviewTemplate = (data: { id: number; imageKey: string; templateType: string; templateLabel: string }, callback: Function) => {
      emit('preview-template', data, callback);
    };

    // Initialize filtered groups with all data when groupedTemplates change
    watch(() => groupedTemplates.value, (newGroups) => {
      filteredGroupedTemplates.value = [...newGroups];
    }, { immediate: true });

    // Load data on mount
    onMounted(() => {
      loadTemplatesData();
      loadWorkflowsData();
    });

    return {
      t,
      filteredGroupedTemplates,
      expandedRows,
      isLoadingWorkflows,
      groupFilterParams,
      groupColumns,
      handleSectionsSelected, // used in ref
      handlePreviewTemplate,
      onGroupFilterChange,
      getGroupFilterStatus,
      clearGroupFilter,
      applyGroupFilter,
    };
  },
});
